import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import Spinner from '@/common/components/Spinner'
import showErrorToast from '@/common/utils/showErrorToast'

import useMutationConfirmTransaction from '@/modules/crypto-invoices/api/hooks/useMutationConfirmTransaction'
import { CryptoInvoiceQueryKeys } from '@/modules/crypto-invoices/keys'

import type { CryptoInvoiceData } from '@/modules/crypto-invoices/type'

interface ConfirmTransactionModalProps {
	invoice: CryptoInvoiceData
	isOpen: boolean
	onClose: () => void
}

export default function ConfirmTransactionModal({
	invoice,
	isOpen,
	onClose,
}: ConfirmTransactionModalProps) {
	const [txHash, setTxHash] = useState('')

	const queryClient = useQueryClient()
	const { mutateAsync: confirmTransaction, isPending } =
		useMutationConfirmTransaction()

	const handleConfirm = async () => {
		if (!txHash.trim()) {
			toast.error('กรุณากรอก Transaction Hash')
			return
		}

		try {
			await confirmTransaction({
				id: invoice.id,
				txHash: txHash.trim(),
			})

			toast.success('ยืนยันธุรกรรมสำเร็จ')

			// Invalidate queries to refresh the table
			queryClient.invalidateQueries({
				queryKey: [CryptoInvoiceQueryKeys.CryptoInvoices],
			})

			// Reset and close
			setTxHash('')
			onClose()
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		setTxHash('')
		onClose()
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>ยืนยันธุรกรรม</DialogTitle>
					<DialogDescription>
						กรอก Transaction Hash เพื่อยืนยันการชำระเงิน
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Transaction ID</Label>
						<div className="rounded-md border bg-muted p-2 font-mono text-sm">
							{invoice.txId}
						</div>
					</div>

					<div className="space-y-2">
						<Label>จำนวนเงิน</Label>
						<div className="rounded-md border bg-muted p-2 text-lg font-semibold">
							$
							{invoice.amount.toLocaleString('en-US', {
								minimumFractionDigits: 2,
							})}
						</div>
					</div>

					{invoice.walletAddress && (
						<div className="space-y-2">
							<Label>กระเป้าเงิน</Label>
							<div className="break-all rounded-md border bg-muted p-2 font-mono text-xs">
								{invoice.walletAddress}
							</div>
						</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="txHash">
							Transaction Hash <span className="text-destructive">*</span>
						</Label>
						<Input
							id="txHash"
							placeholder="0x..."
							value={txHash}
							onChange={(e) => setTxHash(e.target.value)}
							className="font-mono text-sm"
						/>
						<p className="text-xs text-muted-foreground">
							กรอก Transaction Hash ที่ได้รับจากการโอนเงิน
						</p>
					</div>

					<div className="flex justify-end gap-2 pt-4">
						<Button
							variant="outline"
							onClick={handleClose}
							disabled={isPending}
						>
							ยกเลิก
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={isPending || !txHash.trim()}
						>
							{isPending && <Spinner />}
							ยืนยันธุรกรรม
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
